import './index.css'

import cuid from 'cuid'
import React, { useEffect, useReducer, useRef } from 'react'
import { Toaster, toast } from 'sonner'

import NavigationService from '@disco/state-management/NavigationService'
import { setNotificationSystemRef } from '@disco/utils/notifications'

import type { UINotification } from 'types/notification'

import { Flex, Spinner, Text } from 'components/ds'
import ContactUsLink from 'components/ui/contact-us-link'

const LEVEL_BORDER_COLOR: Record<UINotification['level'], string> = {
  success: '#34b57c',
  error: 'tomato',
  warning: '#FFAB00',
}

const notificationDismissDelays: Record<UINotification['level'], number> = {
  success: 2,
  error: 10,
  warning: 5,
}

const itemStyle: React.CSSProperties = {
  background: '#fff',
  borderLeft: '4px solid',
  borderRadius: 3,
  boxShadow: '0 8px 12px rgba(9, 30, 66, 0.15), 0 0 1px rgba(9, 30, 66, 0.30)',
  color: '#172b4d',
  padding: 20,
  maxWidth: 400,
  whiteSpace: 'pre-wrap',
  position: 'relative',
}

const actionStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
  color: '#0052cc',
  cursor: 'pointer',
  fontWeight: 500,
  margin: '5px 0 0',
  padding: 0,
}

const titleStyle: React.CSSProperties = {
  color: 'inherit',
  fontWeight: 500,
  marginBottom: 4,
}

/**
 * Parse notification message string to handle special content markers and interactive elements
 * @param message - Message string that may contain JSON markers for special content
 * @param retry - Retry function to call when retry elements are clicked
 */
export const parseMessage = ({
  message,
  retry,
}: {
  message?: string
  retry: (...args: Array<any>) => any
}): null | Array<
  any | React.ReactElement<'span'> | React.ReactNode | string
> => {
  if (!message) return null
  const content: React.ReactNode[] = []
  message.split('|').forEach(text => {
    if (text.startsWith('{')) {
      const data: {
        retryMessage?: string
        contactUsLink?: string
        intercomMsg?: string
        externalLink?: Record<string, any>
      } = JSON.parse(text)

      if (data.retryMessage) {
        content.push(
          <span className="UINotificationSystem_RetryLabel" onClick={retry}>
            {data.retryMessage}
          </span>,
        )
      }

      if (data.externalLink) {
        content.push(
          <a
            href={data.externalLink.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.externalLink.label}
          </a>,
        )
      }

      if (data.contactUsLink) {
        content.push(
          <ContactUsLink intercomMsg={data.intercomMsg}>
            {data.contactUsLink}
          </ContactUsLink>,
        )
      }
    } else {
      content.push(text)
    }
  })
  return content
}

const navigate = (path: string) => {
  if (NavigationService.isInitialized()) NavigationService.push(path)
}

/**
 * Show a UI notification via sonner. The mapping below deliberately mirrors the
 * old react-notification-system behaviour so existing callers stay unchanged.
 */
const addNotification = (notification: UINotification): void => {
  if (!notification) return

  const id = notification.uid != null ? String(notification.uid) : cuid()

  // A pending notification (e.g. an upload spinner) is later replaced by its result.
  if (notification._replaceNotificationWithUID != null) {
    toast.dismiss(String(notification._replaceNotificationWithUID))
  }

  const retry = () => {
    notification.retry?.()
    toast.dismiss(id)
  }

  // Any `dismissible` value means the user closes it manually, so it never auto-dismisses.
  const seconds = notification.dismissible
    ? 0
    : notification.autoDismiss ?? notificationDismissDelays[notification.level]
  const duration = seconds === 0 ? Infinity : seconds * 1000

  const canDismissOnClick =
    notification.dismissible === 'click' || notification.dismissible === 'both'
  const showDismissButton =
    notification.dismissible === 'button' || notification.dismissible === 'both'

  const action: { label: string; callback: () => void } | undefined =
    !notification.action
      ? undefined
      : 'link' in notification.action
      ? {
          label: notification.action.label,
          callback: () => navigate((notification.action as any).link),
        }
      : notification.action

  const body = notification.spinner ? (
    <Flex align="center" gap="2">
      <Spinner />
      <Text>{notification.message}</Text>
    </Flex>
  ) : (
    parseMessage({ message: notification.message, retry })
  )

  toast.custom(
    () => (
      <div
        className="UINotificationSystem_Item"
        style={{
          ...itemStyle,
          borderLeftColor: LEVEL_BORDER_COLOR[notification.level],
          cursor: canDismissOnClick ? 'pointer' : undefined,
        }}
        onClick={canDismissOnClick ? () => toast.dismiss(id) : undefined}
      >
        {notification.title && (
          <div style={titleStyle}>{notification.title}</div>
        )}
        <div
          className="UINotificationSystem_Message"
          style={showDismissButton ? { paddingRight: 20 } : undefined}
        >
          {/* parseMessage returns an array, so key its entries to satisfy React. */}
          {Array.isArray(body)
            ? body.map((node, i) => (
                <React.Fragment key={i}>{node}</React.Fragment>
              ))
            : body}
        </div>
        {notification.children}
        {action && (
          <button
            type="button"
            style={actionStyle}
            onClick={e => {
              e.stopPropagation()
              action.callback()
            }}
          >
            {action.label}
          </button>
        )}
        {showDismissButton && (
          <button
            type="button"
            aria-label="Dismiss"
            style={{ ...actionStyle, position: 'absolute', top: 8, right: 12 }}
            onClick={e => {
              e.stopPropagation()
              toast.dismiss(id)
            }}
          >
            ×
          </button>
        )}
      </div>
    ),
    { id, duration, unstyled: true },
  )
}

// SLAM-1971: sonner renders toasts globally, so mounting a <Toaster> in each of this
// wrapper's several roots would show every notification multiple times. Track the
// mounted instances and let only the first one render the single <Toaster>.
let mountedInstances: symbol[] = []
const rerenderers = new Set<() => void>()
const notifyAll = () => rerenderers.forEach(fn => fn())

function UINotificationSystem() {
  const instanceIdRef = useRef<symbol | undefined>(undefined)
  if (!instanceIdRef.current) instanceIdRef.current = Symbol('ui-notification')
  const [, forceRender] = useReducer(count => count + 1, 0)

  useEffect(() => {
    const instanceId = instanceIdRef.current!
    mountedInstances.push(instanceId)
    rerenderers.add(forceRender)
    setNotificationSystemRef({ addNotification })
    notifyAll()

    return () => {
      mountedInstances = mountedInstances.filter(id => id !== instanceId)
      rerenderers.delete(forceRender)
      if (mountedInstances.length === 0) setNotificationSystemRef(null)
      notifyAll()
    }
  }, [])

  const isPrimary = mountedInstances[0] === instanceIdRef.current
  if (!isPrimary) return null

  return (
    // Match the old library's "show all, expanded" behaviour. sonner defaults to
    // visibleToasts={3}/expand=false, which would hide the 4th+ toast (including
    // children file-lists) when several fire at once, e.g. multiple upload results.
    <Toaster
      position="bottom-left"
      className="UINotificationSystem"
      expand
      visibleToasts={9}
    />
  )
}

export default UINotificationSystem
